/**
 * Pure grid-placement math for "auto-expand grid on the ghost preview".
 *
 * No Pinia, no Konva — everything here works on plain numbers so it can be
 * unit tested in isolation. The CanvasGrid component projects Konva state
 * (rects, rotations, snap grid) into the `PlacementFootprintCell` shape
 * before calling these functions.
 *
 * Mental model:
 *  - The grid is an axis-aligned rectangle in canvas-pixel coordinates, offset
 *    from the origin by `startOffset` so the canvas has a margin.
 *  - A "footprint" is a list of axis-aligned cells, each at `(x, y, w, h)`,
 *    relative to the grid origin. Multi-tile shapes (e.g. 8-piece layered)
 *    are just longer footprints.
 *  - For 45° squares, layered children are drawn rotated 45°, so their visible
 *    bounding box is `w * sqrt(2)`. We add `w * (sqrt(2) - 1)` per-cell margin
 *    so adjacent candidates don't visually overlap at the corners. This
 *    mirrors the margin already used in `stores/grid.ts:670-678`.
 */

export interface PlacementFootprintCell {
  /** Top-left x relative to the grid origin (i.e. minus startOffset). */
  x: number
  /** Top-left y relative to the grid origin. */
  y: number
  width: number
  height: number
  /** Rotation in degrees; only used to compute the (√2-1) margin in 45° mode. */
  rotation?: number
}

export interface GridPlacementContext {
  gridWidth: number
  gridHeight: number
  startOffset: number
  snap: { x: number; y: number }
  is45Square: boolean
  isLayered: boolean
  /** Hard cap on grid width (px). Beyond this, growth is refused. */
  maxGridWidth: number
  /** Hard cap on grid height (px). Beyond this, growth is refused. */
  maxGridHeight: number
  /**
   * Granularity for visible growth "jumps". After computing the minimum
   * size to fit the footprint, we round up to the next multiple of this
   * so users see a clean, predictable grid resize (e.g. 8ft → 12ft → 16ft)
   * instead of a 1-cell nudge. Default 480px = 4ft, big enough to fit
   * a full 8-piece layered set side-by-side with the existing tiles.
   */
  growthStep?: { x: number; y: number }
}

export interface GridGrowthResult {
  needsExpansion: boolean
  newWidth: number
  newHeight: number
  /** True when the requested growth was larger than maxGrid* and was clamped. */
  capped: boolean
}

export interface DropCandidate {
  /** Anchor (top-left of leader) in grid-relative coordinates. */
  x: number
  y: number
  /** All cells covered at this anchor. */
  cells: PlacementFootprintCell[]
}

const SQRT2_MINUS_1 = Math.sqrt(2) - 1

/**
 * Round `value` up to the next multiple of `step` that is strictly greater
 * than `current`. If `value` is already past one step boundary past
 * `current`, the next boundary is used. This gives "round to next 2ft" so
 * users see a clean 8ft → 10ft → 12ft growth instead of a 1-cell nudge.
 */
function roundUpToStep(value: number, step: number, current: number): number {
  if (value <= current) return current
  // Bump value up to the next step boundary strictly greater than current.
  const stepsPastCurrent = Math.ceil((value - current) / step)
  return current + stepsPastCurrent * step
}

/**
 * Returns the per-cell margin used for layered shapes on the 45° grid.
 * Mirrors the math in `stores/grid.ts:670-678`.
 */
function layeredCornerMargin(cell: PlacementFootprintCell, is45Square: boolean, isLayered: boolean): number {
  if (!is45Square || !isLayered) return 0
  // The visible bounding box of a square rotated 45° is w * √2, so each side
  // grows by w * (√2 - 1). The dominant axis is the larger of width/height.
  const w = Math.max(cell.width, cell.height)
  return w * SQRT2_MINUS_1
}

/**
 * Translate a list of cells by an (x, y) offset. Returns new objects.
 */
export function offsetCells(cells: PlacementFootprintCell[], dx: number, dy: number): PlacementFootprintCell[] {
  return cells.map(c => ({ ...c, x: c.x + dx, y: c.y + dy }))
}

/**
 * AABB collision check between two cells, with optional extra per-cell margin.
 * Returns true if the cells overlap (with margin treated as "extra padding").
 */
export function cellsCollide(a: PlacementFootprintCell, b: PlacementFootprintCell, marginA = 0, marginB = 0): boolean {
  const aLeft = a.x - marginA
  const aRight = a.x + a.width + marginA
  const aTop = a.y - marginA
  const aBottom = a.y + a.height + marginA

  const bLeft = b.x - marginB
  const bRight = b.x + b.width + marginB
  const bTop = b.y - marginB
  const bBottom = b.y + b.height + marginB

  return !(aRight <= bLeft || aLeft >= bRight || aBottom <= bTop || aTop >= bBottom)
}

/**
 * Does a footprint (list of cells) fit within the grid bounds?
 * Grid-relative coordinates: a cell with x=0, y=0 is at the top-left of the
 * playable area (i.e. at `startOffset` in absolute canvas coords).
 */
export function footprintFitsGrid(
  cells: PlacementFootprintCell[],
  gridWidth: number,
  gridHeight: number,
  margin: (c: PlacementFootprintCell) => number,
): boolean {
  for (const c of cells) {
    const m = margin(c)
    if (c.x - m < 0) return false
    if (c.y - m < 0) return false
    if (c.x + c.width + m > gridWidth) return false
    if (c.y + c.height + m > gridHeight) return false
  }
  return true
}

/**
 * Does a footprint collide with any committed footprint on the grid?
 * Per-cell layered-margin is applied on both sides.
 */
export function footprintCollidesWith(
  cells: PlacementFootprintCell[],
  committed: PlacementFootprintCell[],
  margin: (c: PlacementFootprintCell) => number,
): boolean {
  for (const placed of cells) {
    const mPlaced = margin(placed)
    for (const other of committed) {
      if (cellsCollide(placed, other, mPlaced, margin(other))) return true
    }
  }
  return false
}

/**
 * Generate the cells covered by a (possibly multi-tile) shape at the given
 * anchor. `templateCells` is the shape's cell layout with the leader at
 * `(0, 0)`. We translate the whole template by `(anchorX, anchorY)`.
 */
export function generateFootprint(
  templateCells: PlacementFootprintCell[],
  anchorX: number,
  anchorY: number,
): PlacementFootprintCell[] {
  return offsetCells(templateCells, anchorX, anchorY)
}

/**
 * Compute the per-cell margin function for a context. Centralizes the
 * 45° + layered logic so the candidate search and growth math stay consistent.
 */
export function makeMarginFn(context: GridPlacementContext): (c: PlacementFootprintCell) => number {
  return (c) => layeredCornerMargin(c, context.is45Square, context.isLayered)
}

/**
 * Enumerate valid anchor positions for a shape's footprint, snapped to the
 * grid, filtered by collision against committed shapes, ordered by distance
 * from `cursorPos`. The search is a spiral outward from the cursor's snapped
 * cell, capped at `maxCandidates`.
 *
 * Inputs are all in grid-relative coordinates (subtract `startOffset` before
 * calling). Returned anchors are also grid-relative.
 */
export function findValidDropCandidates(
  templateCells: PlacementFootprintCell[],
  cursorPos: { x: number; y: number },
  committed: PlacementFootprintCell[],
  context: GridPlacementContext,
  maxCandidates = 16,
): DropCandidate[] {
  if (templateCells.length === 0) return []
  const margin = makeMarginFn(context)
  const { snap, gridWidth, gridHeight } = context

  // Snap the cursor to the grid first so the search starts from the
  // nearest cell.
  const cursorX = Math.round(cursorPos.x / snap.x) * snap.x
  const cursorY = Math.round(cursorPos.y / snap.y) * snap.y

  // Bounding box of the template (in template-local coordinates).
  let tmplMinX = Infinity, tmplMinY = Infinity, tmplMaxX = -Infinity, tmplMaxY = -Infinity
  for (const c of templateCells) {
    if (c.x < tmplMinX) tmplMinX = c.x
    if (c.y < tmplMinY) tmplMinY = c.y
    if (c.x + c.width > tmplMaxX) tmplMaxX = c.x + c.width
    if (c.y + c.height > tmplMaxY) tmplMaxY = c.y + c.height
  }

  // Pre-snap committed cells' margins so we don't recompute on every candidate.
  const committedWithMargin = committed.map(c => ({ cell: c, margin: margin(c) }))

  const result: DropCandidate[] = []
  const seen = new Set<string>()

  // Spiral search: layer 0 is just the cursor's snapped cell. Each
  // subsequent layer expands the search radius by `snap`. Stop when the
  // radius exceeds the grid dimensions (we'd never find anything further out).
  const maxRadius = Math.max(gridWidth, gridHeight)
  let layer = 0
  while (result.length < maxCandidates) {
    const radius = layer * Math.max(snap.x, snap.y)
    if (radius > maxRadius) break

    // Generate candidate anchors for this layer.
    const candidates: Array<{ x: number; y: number }> = []
    if (layer === 0) {
      candidates.push({ x: cursorX, y: cursorY })
    } else {
      // Square ring: walk along the perimeter of the (radius * 2) box
      // around the cursor.
      for (let dx = -radius; dx <= radius; dx += snap.x) {
        candidates.push({ x: cursorX + dx, y: cursorY - radius })
        candidates.push({ x: cursorX + dx, y: cursorY + radius })
      }
      for (let dy = -radius + snap.y; dy <= radius - snap.y; dy += snap.y) {
        candidates.push({ x: cursorX - radius, y: cursorY + dy })
        candidates.push({ x: cursorX + radius, y: cursorY + dy })
      }
    }

    // Sort by distance to cursor so closer candidates come first.
    candidates.sort((a, b) => {
      const da = (a.x - cursorPos.x) ** 2 + (a.y - cursorPos.y) ** 2
      const db = (b.x - cursorPos.x) ** 2 + (b.y - cursorPos.y) ** 2
      return da - db
    })

    for (const cand of candidates) {
      if (result.length >= maxCandidates) break
      // De-dupe snapped cells.
      const key = `${cand.x}:${cand.y}`
      if (seen.has(key)) continue
      seen.add(key)

      const cells = generateFootprint(templateCells, cand.x, cand.y)
      if (!footprintFitsGrid(cells, gridWidth, gridHeight, margin)) continue

      // Re-check collision against committed shapes with the per-cell margin.
      let collides = false
      for (const placed of cells) {
        const mPlaced = margin(placed)
        for (const other of committedWithMargin) {
          if (cellsCollide(placed, other.cell, mPlaced, other.margin)) {
            collides = true
            break
          }
        }
        if (collides) break
      }
      if (collides) continue

      result.push({ x: cand.x, y: cand.y, cells })
    }

    layer += 1
    // Safety guard: don't spiral past the grid bounds.
    if (layer * Math.max(snap.x, snap.y) > Math.max(gridWidth, gridHeight) * 2) break
  }

  return result
}

/**
 * Compute the minimum new grid size to fit a footprint at its current
 * position. Grows toward the cursor (right if cursor right of grid, down
 * if below, both if both). All inputs/outputs are absolute canvas
 * coordinates (NOT grid-relative).
 *
 * Caller is responsible for translating between absolute and grid-relative
 * coordinates. This function returns absolute grid dimensions.
 */
export function computeRequiredGridSize(
  cells: PlacementFootprintCell[],
  cursorAbs: { x: number; y: number },
  context: GridPlacementContext,
): GridGrowthResult {
  if (cells.length === 0) {
    return {
      needsExpansion: false,
      newWidth: context.gridWidth,
      newHeight: context.gridHeight,
      capped: false,
    }
  }
  const margin = makeMarginFn(context)
  const { gridWidth, gridHeight, snap, maxGridWidth, maxGridHeight } = context
  const growthStep = context.growthStep ?? { x: 480, y: 480 } // default 4ft

  // Compute footprint extents in absolute canvas coordinates.
  let minAbsX = Infinity, minAbsY = Infinity, maxAbsX = -Infinity, maxAbsY = -Infinity
  for (const c of cells) {
    const m = margin(c)
    // Convert grid-relative cell to absolute by adding startOffset.
    const absLeft = context.startOffset + c.x - m
    const absTop = context.startOffset + c.y - m
    const absRight = context.startOffset + c.x + c.width + m
    const absBottom = context.startOffset + c.y + c.height + m
    if (absLeft < minAbsX) minAbsX = absLeft
    if (absTop < minAbsY) minAbsY = absTop
    if (absRight > maxAbsX) maxAbsX = absRight
    if (absBottom > maxAbsY) maxAbsY = absBottom
  }

  // Decide direction of growth based on cursor position relative to grid edges.
  // If cursor is to the right of current grid → grow right.
  // If cursor is below → grow down.
  // If cursor is left of startOffset or above 0 → we'd need to shift the offset,
  // which is out of scope for v1; treat as "capped" with the largest size that
  // fits the right/bottom side instead.
  const cursorRightOfGrid = cursorAbs.x > gridWidth
  const cursorBelowGrid = cursorAbs.y > gridHeight
  const cursorLeftOfOrigin = cursorAbs.x < context.startOffset
  const cursorAboveOrigin = cursorAbs.y < context.startOffset

  let newWidth = gridWidth
  let newHeight = gridHeight
  let needsExpansion = false
  let capped = false

  if (cursorRightOfGrid) {
    const minWidth = Math.ceil(maxAbsX / snap.x) * snap.x
    newWidth = roundUpToStep(minWidth, growthStep.x, gridWidth)
    needsExpansion = true
  }
  if (cursorBelowGrid) {
    const minHeight = Math.ceil(maxAbsY / snap.y) * snap.y
    newHeight = roundUpToStep(minHeight, growthStep.y, gridHeight)
    needsExpansion = true
  }

  // If the footprint extends past the right/bottom regardless of cursor,
  // we still need to grow to make room (e.g. ghost is at the right edge).
  if (maxAbsX > newWidth) {
    const minWidth = Math.ceil(maxAbsX / snap.x) * snap.x
    newWidth = roundUpToStep(minWidth, growthStep.x, gridWidth)
    needsExpansion = true
  }
  if (maxAbsY > newHeight) {
    const minHeight = Math.ceil(maxAbsY / snap.y) * snap.y
    newHeight = roundUpToStep(minHeight, growthStep.y, gridHeight)
    needsExpansion = true
  }

  // Left/up overflow → out of scope for v1. Flag as capped so the UI can
  // show a "grid is full" toast instead of silently dropping the shape.
  if (cursorLeftOfOrigin || cursorAboveOrigin) {
    capped = true
  }

  // Apply hard caps.
  if (newWidth > maxGridWidth) {
    newWidth = maxGridWidth
    capped = true
  }
  if (newHeight > maxGridHeight) {
    newHeight = maxGridHeight
    capped = true
  }

  return {
    needsExpansion,
    newWidth,
    newHeight,
    capped,
  }
}

/**
 * Helper: build a single-cell template from a leader's dimensions.
 * Useful when you have a leader shape and need to feed `findValidDropCandidates`.
 */
export function singleCellTemplate(width: number, height: number, rotation = 0): PlacementFootprintCell[] {
  return [{ x: 0, y: 0, width, height, rotation }]
}
